import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Trash2, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../lib/api';

const ROLE_OPTS = [
  ['territory_manager', 'Todos los TMs'],
  ['agent', 'Todos los agentes'],
  ['capturer', 'Todos los capturadores'],
];

export default function AdminComms() {
  const [comms, setComms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [roles, setRoles] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([api.get('/comms'), api.get('/users')]);
      setComms(r1.data || []);
      setUsers((r2.data || []).filter(x => x.status === 'active'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleRole = (r) => setRoles(s => s.includes(r) ? s.filter(x => x !== r) : [...s, r]);
  const toggleUser = (id) => setUserIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const send = async () => {
    if (!body.trim()) { toast.error('Escribe el mensaje'); return; }
    if (!roles.length && !userIds.length) { toast.error('Selecciona destinatarios'); return; }
    setSending(true);
    try {
      const r = await api.post('/comms', { title, body, roles, recipient_user_ids: userIds });
      toast.success(`Comunicado enviado a ${r.data.recipients.length} persona(s)`);
      setTitle(''); setBody(''); setRoles([]); setUserIds([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  const remove = async (id) => {
    await api.delete(`/comms/${id}`);
    setComms(cs => cs.filter(x => x.id !== id));
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="max-w-3xl" data-testid="admin-comms-page">
      <h1 className="font-serif text-[30px] tracking-tight mb-1">Comunicados</h1>
      <p className="text-[13px] text-[#5a5a5a] mb-6">Mensajes directos a los destinatarios que elijas (no amarrados a estaciones) — ej. RSOP para comunicar a dealers. Les llega por notificación y aparece en su zona de Cambios hasta que confirmen lectura.</p>

      <div className="bg-white border border-[#e8e5de] rounded-xl p-5 mb-6 space-y-3">
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título (opcional)…" className="bg-[#fafaf6]" data-testid="comm-title"/>
        <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Mensaje — ej. RSOP sur/norte FOB y fecha del movimiento de factura…" className="bg-[#fafaf6] min-h-[110px]" data-testid="comm-body"/>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#8a8579] mb-1.5">Por rol</p>
          <div className="flex flex-wrap gap-2">
            {ROLE_OPTS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => toggleRole(k)}
                className={`text-[12px] px-3 py-1.5 rounded-full border ${roles.includes(k) ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#5a5a5a] border-[#e8e5de]'}`}
                data-testid={`comm-role-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#8a8579] mb-1.5">Personas específicas</p>
          <div className="max-h-44 overflow-y-auto border border-[#e8e5de] rounded-lg divide-y divide-[#f0ede5]">
            {users.map(x => (
              <label key={x.id} className="flex items-center gap-2.5 px-3 py-2 text-[12.5px] cursor-pointer hover:bg-[#fafaf6]">
                <input type="checkbox" checked={userIds.includes(x.id)} onChange={() => toggleUser(x.id)} className="accent-[#1c1c1c]" data-testid={`comm-user-${x.id}`}/>
                <span className="flex-1">{x.name}</span>
                <span className="text-[10.5px] text-[#8a8579]">{x.role}</span>
              </label>
            ))}
          </div>
        </div>
        <Button onClick={send} disabled={sending} className="h-10 rounded-full bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="comm-send-btn">
          {sending ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Send size={14} className="mr-1.5"/>}Enviar comunicado
        </Button>
      </div>

      <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579] mb-2">Enviados</p>
      <div className="space-y-3">
        {comms.length === 0 && <p className="text-[13px] text-[#8a8579]">Aún no has enviado comunicados.</p>}
        {comms.map(c => {
          const total = (c.recipients || []).length;
          const read = Object.keys(c.read_by || {}).length;
          const pending = (c.recipients || []).filter(r => !(c.read_by || {})[r.id]);
          return (
            <div key={c.id} className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid={`comm-sent-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {c.title && <p className="text-[14px] font-semibold">{c.title}</p>}
                  <p className="text-[12.5px] text-[#3a3a3a] whitespace-pre-wrap mt-0.5">{c.body}</p>
                </div>
                <button onClick={() => remove(c.id)} className="text-[#b91c1c] p-1.5 rounded hover:bg-[#fee2e2] shrink-0" data-testid={`comm-del-${c.id}`}><Trash2 size={14}/></button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${read === total ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fef3c7] text-[#92400e]'}`} data-testid={`comm-read-count-${c.id}`}>
                  {read === total ? <CheckCircle2 size={11}/> : <Clock size={11}/>} Leído {read}/{total}
                </span>
                {pending.length > 0 && (
                  <span className="text-[10.5px] text-[#8a8579]">Pendientes: {pending.map(p => p.name).join(', ')}</span>
                )}
                <span className="text-[10.5px] text-[#8a8579] ml-auto">{new Date(c.created_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
